import {
  arrayRemove,
  arrayUnion,
  collection,
  CollectionReference,
  deleteField,
  doc,
  DocumentSnapshot,
  getCountFromServer,
  limit,
  updateDoc,
} from '@firebase/firestore';
import { db, functions, httpsCallable } from '@util/firebase';
import { nonNullable } from '@util/index';
import { logError } from '@util/logError';
import { CarrierCode } from '@util/maps/carriers';
import {
  OrderDocument,
  orderDocumentSchema,
  OrderItemDocument,
  OrderNoteType,
  OrderStates,
  OrderStateType,
} from '@util/types/firestore/order';
import {
  endBefore,
  getDoc,
  getDocs,
  limitToLast,
  orderBy,
  query,
  QueryConstraint,
  startAfter,
  where,
} from 'firebase/firestore';
import { z } from 'zod';
import {
  parseRefundOrderResult,
  parseReleaseRefundPayoutHoldResult,
  refundIntentDocumentId,
  ReleaseRefundPayoutHoldResult,
} from './refundOrder.contract';

export const ordersRef = collection(
  db,
  'orders'
) as CollectionReference<OrderDocument>;

const getRef = (id: string) => doc<OrderDocument>(ordersRef, id);

/**
 * web#388 item 4 (web half), Fix I (Codex — missed by both the ruling's own
 * author and 5 prior reviews). `orderDocumentSchema`'s `created` field
 * defaults to `Date.now()` (order.ts:125) when the raw document has none —
 * so by the time ANY reader calls `.parse()`, a document missing `created`
 * is indistinguishable from one whose creation date is genuinely right
 * now, comfortably post-acquisition. `orderItemToStateType` /
 * `isEligibleForPartialRefundLabel` (StatusBadge.tsx) gate the "Partially
 * Refunded" label to post-acquisition orders and are documented to fail
 * toward "do not show the label" when the date is missing — but that
 * fail-safe can never fire against a value that's already been defaulted.
 * The population most likely to have a missing/inconsistent `created` is
 * exactly the pre-acquisition set this ruling exists to protect.
 *
 * Deliberately NOT fixed by changing `created`'s default, and NOT by
 * widening `orderDocumentSchema`'s TYPE (an earlier attempt wrapped it in
 * `z.preprocess`, which silently breaks `.extend()`/`.pick()` — both used
 * elsewhere on this exact schema, e.g. `confirmationOrder.ts:112-113`,
 * `payments.types.ts:37` — a real, confirmed blast-radius hit, caught by
 * `tsc` before it shipped). This computes presence from the RAW Firestore
 * value BEFORE `.parse()` ever runs, matching the precedent already
 * established in `confirmationOrder.ts`'s own freshness gate ("checked
 * against the RAW Firestore value, not the schema-parsed one... a doc
 * missing `created` fails closed"), and attaches it as an extra,
 * runtime-only field via a plain object spread — `orderDocumentSchema`'s
 * definition and type are completely untouched. Consumers that don't know
 * about this field are unaffected; the three `orderItemToStateType` render
 * call sites (OrderDetailsPage.tsx, PurchaseCard.tsx, OrderCard.tsx) read
 * it via a runtime-narrowed cast, the same convention already used for
 * OrderItemDocument's own untyped passthrough fields
 * (OrderDetailsPage.tsx's `payoutHoldLine`).
 */
const hasVerifiedCreatedTimestamp = (raw: unknown): boolean =>
  !!raw &&
  typeof raw === 'object' &&
  typeof (raw as { created?: unknown }).created === 'number' &&
  Number.isFinite((raw as { created?: unknown }).created);

/**
 * A plain intersection, not a schema change — `OrderDocument` itself is
 * untouched. Only `getOrderById` and `getAllBuyerOrdersPaginated` (the two
 * live call sites that actually run `orderDocumentSchema.parse()` for the
 * order-detail and purchases pages) return this widened shape; every other
 * `OrderDocument` consumer is unaffected.
 */
export type OrderDocumentWithCreatedProvenance = OrderDocument & {
  created_is_verified: boolean;
};

/**
 * Reads `created_is_verified` off an order that MAY carry it — only
 * `getOrderById` / `getAllBuyerOrdersPaginated` attach it (see
 * `OrderDocumentWithCreatedProvenance` above); `OrderDocument`'s own type
 * is untouched, so this narrows at runtime rather than trusting a declared
 * type, the same convention already used for `OrderItemDocument`'s own
 * untyped passthrough fields (OrderDetailsPage.tsx's `payoutHoldLine`).
 * Every one of the three `orderItemToStateType` render call sites
 * (OrderDetailsPage.tsx, PurchaseCard.tsx, OrderCard.tsx) should pass its
 * `orderCreatedAt` argument through this, never `order.created` directly —
 * a missing, absent, or wrong-shape flag degrades to `undefined`, which is
 * exactly the safe "do not show the label" direction
 * `isEligibleForPartialRefundLabel` (StatusBadge.tsx) already wants for an
 * unknown date.
 */
export const verifiedOrderCreatedAt = (
  order: Pick<OrderDocument, 'created'>
): number | undefined =>
  (order as { created_is_verified?: unknown }).created_is_verified === true
    ? order.created
    : undefined;

export const getOrderById = async (
  id: string
): Promise<OrderDocumentWithCreatedProvenance | null> => {
  const snapshot = await getDoc(getRef(id));
  try {
    const raw = snapshot.data();
    const data = orderDocumentSchema.parse(raw);
    return { ...data, created_is_verified: hasVerifiedCreatedTimestamp(raw) };
  } catch (e) {
    logError(e);
    return null;
  }
};

export const markAsDelivered = (order: OrderDocument) => {
  return updateDoc(getRef(order.id), {
    sellers: order.sellers,
    total_state: 1,
    updated: Date.now(),
    tracking_error: deleteField(),
  });
};

export const updateOrderByKey = async <
  T extends OrderDocument[keyof OrderDocument],
>(
  orderId: string,
  key: keyof OrderDocument,
  value: T
) => {
  const updateData = {
    [key]: value,
    updated: Date.now(),
  } as Partial<OrderDocument>;
  await updateDoc(getRef(orderId), updateData);
};

export const updateOrderItemState = async (
  order: OrderDocument,
  sellerId: string,
  itemId: string,
  state: number
) => {
  // Extracts the sellers from the order.
  const sellers = order.sellers;

  // Finds the index of the item within the seller's list.
  const index =
    sellers?.[sellerId].findIndex((item) => item.id === itemId) ?? -1;

  // Checks if the item was found (index >= 0) and if the sellers list exists.
  if (index >= 0 && sellers) {
    // delivery_time handling (GearBoard #1308). The automatic ShipEngine
    // trackers set state=2 + delivery_time atomically; this admin path now
    // matches that contract. We also clear delivery_time when an admin
    // reverts an item away from Delivered, so a future re-flip captures
    // a fresh timestamp instead of an obsolete one (Codex PR #1335 P2).
    const previousState = sellers[sellerId][index].state;

    if (state === 2 && previousState !== 2) {
      // Entering Delivered from another state — stamp fresh delivery_time
      sellers[sellerId][index].delivery_time = Date.now();
    } else if (
      state !== 2 &&
      state !== 3 &&
      sellers[sellerId][index].delivery_time != null
    ) {
      // Reverting away from Delivered (state 2 -> 0/1/4/5/6/7) — remove so
      // a future legitimate re-entry to Delivered doesn't reuse the stale
      // timestamp. State 3 (Complete, seller paid) preserves delivery_time
      // as a historical fact.
      delete sellers[sellerId][index].delivery_time;
    }
    // state === 2 with previousState === 2: no-op (preserve existing timestamp)

    // Updates the state of the specific item.
    sellers[sellerId][index].state = state;

    // Initializes a flag to check if all items are complete (state >= 3).
    let allComplete = true;

    // A set to keep track of unique states across all items.
    const states = new Set<number>();

    // Loops through each seller's items to update states and check completion.
    Object.values(sellers).forEach((items) =>
      items.forEach((item) => {
        // Adds the item's state to the set.
        states.add(item.state);
        // If any item's state is less than 3, sets allComplete to false.
        if (item.state < 3) allComplete = false;
      })
    );

    // Initializes a variable to hold the total state of the order.
    let total_state = order.total_state;

    // Updates total_state based on the states of individual items.
    if (states.has(6) || states.has(7)) {
      // If any item is in state 6 or 7, sets total order state to 3.
      total_state = 3;
    } else if (allComplete) {
      // If all items are complete, sets total order state to 2.
      total_state = 2;
    } else if (states.has(2)) {
      // If any item is in state 2, sets total order state to 1.
      total_state = 1;
    } else {
      // If none of the above, sets total order state to 0.
      total_state = 0;
    }

    // Updates the order document with the new sellers state and total state.
    await updateDoc(getRef(order.id), {
      sellers,
      total_state,
      updated: Date.now(),
    });
  }
};

export const getOrderByOrderNum = async (
  order_num: number
): Promise<OrderDocument | null> => {
  const q = query(ordersRef, where('order_num', '==', order_num), limit(1));
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map((doc) => {
    return orderDocumentSchema.parse(doc.data());
  });
  if (data) {
    return data[0];
  }
  return null;
};

export const getAllBuyerOrders = async (uid: string, isAdmin = false) => {
  const q = isAdmin
    ? query(ordersRef, orderBy('created', 'desc'))
    : query(
        ordersRef,
        where('buyer_id', '==', uid),
        orderBy('created', 'desc')
      );
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map((doc) => {
    // return orderDocumentSchema.parse(doc.data()); // TODO: fix this
    return doc.data();
  });

  if (typeof data === 'undefined') {
    return { results: null };
  }
  return { results: data };
};

async function getValidCursorOrNull(
  cursor: string,
  direction: 'next' | 'prev',
  constraints: QueryConstraint[] = []
) {
  if (!cursor) return null;
  const cursorSnapshot = await getDoc(doc(ordersRef, cursor));
  const startAfterOrEndBefore = direction === 'next' ? startAfter : endBefore;
  const q = query(
    ordersRef,
    ...constraints,
    startAfterOrEndBefore(cursorSnapshot)
  );
  const snapshot = await getDocs(q);
  const document = snapshot.docs[0];
  if (!document) {
    return null;
  }
  return cursor;
}

interface GetOrdersArgs {
  uid: string;
  isAdmin?: boolean;
  adminUid?: string | null; // uid to get orders for as admin
  lastKey?: string | null;
  orderStatus: OrderStateType | 'all';
}
export const getAllBuyerOrdersPaginated = async (args: GetOrdersArgs) => {
  const { uid, isAdmin, adminUid, lastKey, orderStatus } = args;
  if (!uid && !isAdmin) return Promise.resolve({ results: [] });

  const limitValue = 8;
  const lastDoc = lastKey ? await getDoc(doc(ordersRef, lastKey)) : null;

  const filterConstraints = [
    ...(orderStatus !== 'all'
      ? [where('states', 'array-contains', OrderStates.indexOf(orderStatus))]
      : []),
  ];

  const getAll = (lastDoc: DocumentSnapshot<OrderDocument> | null) =>
    query(
      ordersRef,
      orderBy('created', 'desc'),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      ...filterConstraints,
      limit(limitValue)
    );

  const getByUid = (
    buyerId: string,
    lastDoc: DocumentSnapshot<OrderDocument> | null
  ) =>
    query(
      ordersRef,
      where('buyer_id', '==', buyerId),
      orderBy('created', 'desc'),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      ...filterConstraints,
      limit(limitValue)
    );

  const q =
    adminUid && isAdmin
      ? getByUid(adminUid, lastDoc)
      : isAdmin
        ? getAll(lastDoc)
        : getByUid(uid, lastDoc);
  const snapshot = await getDocs(q);
  // web#388 item 4 (web half), Fix I — see the long comment on
  // `hasVerifiedCreatedTimestamp` above `getOrderById`. This is the other
  // live call site that feeds a render path (PurchaseCard.tsx) reading
  // `orderItemToStateType`'s acquisition-date gate.
  const data: OrderDocumentWithCreatedProvenance[] = snapshot.docs.map(
    (doc) => {
      const raw = doc.data();
      return {
        ...orderDocumentSchema.parse(raw),
        created_is_verified: hasVerifiedCreatedTimestamp(raw),
      };
    }
  );
  return { results: data };
};

// TODO: This is not being used. Can be removed?
export const getOrderStateNumberFromStatus = (status: string) => {
  switch (status) {
    case 'orderPlaced':
      return 0;
    case 'delivered':
      return 1;
    case 'complete':
      return 2;
    case 'onHold':
      return 3;
    case 'returnRequested':
      return 4;
  }
};

export const getAllSellerOrdersPaginated = async (args: {
  uid: string;
  isAdmin?: boolean;
  adminUid?: string | null; // uid to get orders for as admin
  lastKey?: string;
  limit?: number;
  pageInfo?: {
    cursor?: string;
    direction?: 'next' | 'prev';
  };
}) => {
  const resultsLimit = args.limit ?? 5;
  let cursorConstraint: QueryConstraint | undefined;
  let limitOrLimitToLast = limit;
  if (args.pageInfo && args.pageInfo.cursor && args.pageInfo.direction) {
    const { cursor, direction } = args.pageInfo;
    const startAfterOrEndBefore = direction === 'next' ? startAfter : endBefore;
    const cursorSnapshot = await getDoc(doc(ordersRef, cursor));
    cursorConstraint = startAfterOrEndBefore(cursorSnapshot);
    limitOrLimitToLast = direction === 'prev' ? limitToLast : limit;
  }

  const contraintsUnlimited = [
    !args.isAdmin || args.adminUid
      ? where('seller_arr', 'array-contains', args.adminUid || args.uid)
      : null,
    orderBy('created', 'desc'),
  ].filter(nonNullable);

  const constraintsWithCursor = [
    ...contraintsUnlimited,
    cursorConstraint,
  ].filter(nonNullable);

  const qLimited = query(
    ordersRef,
    ...constraintsWithCursor,
    limitOrLimitToLast(resultsLimit)
  );
  const qUnlimited = query(ordersRef, ...contraintsUnlimited);

  const limitedPromise = getDocs(qLimited);
  const unlimitedPromise = getCountFromServer(qUnlimited);

  const [limitedSnapshot, totalResultsSnapshot] = await Promise.all([
    limitedPromise,
    unlimitedPromise,
  ]);

  const results = limitedSnapshot.docs.map((doc) => doc.data());
  const prevId = results[0]?.id;
  const nextId = results[results.length - 1]?.id;
  const [prev, next] = await Promise.all([
    getValidCursorOrNull(prevId, 'prev', contraintsUnlimited),
    getValidCursorOrNull(nextId, 'next', contraintsUnlimited),
  ]);
  const totalResults = totalResultsSnapshot.data().count;

  return {
    results,
    pageInfo: {
      totalResults,
      currentCursor: args.pageInfo?.cursor ?? null,
      direction: args.pageInfo?.direction ?? null,
      next,
      prev,
    },
  };
};

export const getAllSellerOrders = async (uid: string) => {
  // where uid in seller_arr
  const q = query(
    ordersRef,
    where('seller_arr', 'array-contains', uid),
    orderBy('created', 'desc')
  );
  const snapshot = await getDocs(q);
  const data = snapshot.docs.map((doc) => {
    // return orderDocumentSchema.parse(doc.data());
    return doc.data();
  });

  if (typeof data === 'undefined') {
    return { results: null };
  }
  return { results: data };
};

export const getSellerOrdersByState = async (uid: string, state: number) => {
  const q = query(
    ordersRef,
    where('seller_arr', 'array-contains', uid),
    where('total_state', '==', state),
    orderBy('created', 'desc')
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data());
  return { results };
};

export async function getPendingOrders(seller_id: string) {
  const q = query(
    ordersRef,
    where('seller_arr', 'array-contains', seller_id),
    where('total_state', 'in', [0, 1])
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data());
  return results;
}

export function getOrderId(): string {
  const newDocRef = doc(ordersRef);
  const id = newDocRef.id;
  return id;
}

// getOrderNum() was removed with GB #1404. It minted a "temp" order number by
// calling the orders-getOrderNumber callable purely to satisfy a required
// schema field before payment. That burned the seller's order counter on every
// attempt and produced the throwaway number the confirmation page mistakenly
// displayed. The real order_num is generated once, server-side, in the
// paymentSuccess webhook. Do not reintroduce a client-side order-number mint.

export async function getOrderByPaymentId(payment_id: string) {
  const q = query(ordersRef, where('payment_id', '==', payment_id), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : snap.docs[0].data();
}

export async function getOrderAdmin(orderNumOrStripeId: string) {
  const c = orderNumOrStripeId.startsWith('ch_')
    ? where('payment_id', '==', orderNumOrStripeId)
    : where('order_num', '==', Number(orderNumOrStripeId));
  const q = query(ordersRef, c, limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : snap.docs[0].data();
}

/** One item whose seller payout could not be completed (api#303 PR 1.5). */
export type MarkAsReceivedFailedItem = {
  /** The product id of the item that was not paid. */
  item_id: string;
  /** Stripe error code, when there was one. Never raw Stripe prose. */
  code?: string;
};

/**
 * What `markAsReceived` resolves to.
 *
 * `partial` means SOME seller payouts failed while others went through. It is
 * a resolved response, not a rejection, so a caller that ignores the payload
 * shows a success screen over a real payment failure — which is how the
 * 2026-07-19 non-payment stayed invisible. `null` comes back when the callable
 * found nothing eligible to pay and did nothing at all.
 */
export type MarkAsReceivedResult = {
  data: 'success' | 'partial';
  paid_count: number;
  failed_items?: MarkAsReceivedFailedItem[];
} | null;

// product_ids are the items the buyer is marking as received
export async function markAsReceived(
  orderId: string,
  product_ids: string[]
): Promise<MarkAsReceivedResult> {
  const res = await httpsCallable<
    { id: string; product_ids: string[] },
    MarkAsReceivedResult
  >(
    functions,
    'markAsReceived'
  )({ id: orderId, product_ids });
  return res.data;
}

export async function addNewNote(orderId: string, note: string): Promise<void> {
  // add a new note to the order
  const newNote: OrderNoteType = {
    text: note,
    created: Date.now(),
  };
  await updateDoc(doc(ordersRef, orderId), {
    notes: arrayUnion(newNote),
  });
}

export async function deleteNote(
  orderId: string,
  note: OrderNoteType
): Promise<void> {
  // delete a note from the order
  await updateDoc(doc(ordersRef, orderId), {
    notes: arrayRemove(note),
  });
}

interface StartTrackingArgs {
  carrier: CarrierCode;
  trackingNo: string;
  orderId: string;
  sellerId: string;
  productId: string;
}

interface StartTrackingError {
  status: 'error';
  message: string;
}

interface StartTrackingSuccess {
  status: 'success';
}

export type StartTrackingResponse = StartTrackingError | StartTrackingSuccess;
export async function startTracking(
  data: StartTrackingArgs
): Promise<StartTrackingResponse> {
  data.trackingNo = data.trackingNo.trim();
  // call the startTracking firebase function
  try {
    await httpsCallable<StartTrackingArgs, { status: 'success' }>(
      functions,
      'startTracking'
    )(data);
    return {
      status: 'success',
    };
  } catch (e) {
    logError(e);
    if (
      !!e &&
      typeof e === 'object' &&
      'message' in e &&
      typeof e.message === 'string'
    ) {
      return {
        status: 'error',
        message: e.message,
      };
    }
    return {
      status: 'error',
      message: 'Unknown error',
    };
  }
}

type ShopOverviewResponse = {
  data: {
    week: {
      chart: [number, number][];
      total: number;
    };
    month: {
      chart: [number, number][];
      total: number;
    };
    lmonth: {
      chart: [number, number][];
      total: number;
    };
    year: {
      chart: [number, number][];
      total: number;
    };
    lyear: {
      chart: [number, number][];
      total: number;
    };
    all: {
      chart: [number, number][];
      total: number;
    };
  };
  recent_orders: OrderDocument[];
  pending_orders: number;
  completed_orders: number;
  available_balance: number;
  actual_pending: number;
  currency: string;
};

export async function getShopOverview() {
  const res = await httpsCallable<Record<string, never>, ShopOverviewResponse>(
    functions,
    'getShopOverview'
  )({});
  return res.data;
}

export const reasons = [
  z.literal('duplicate'),
  z.literal('fraudulent'),
  z.literal('requested_by_customer'),
  z.literal('requested_by_seller'),
  z.literal('product_return'),
  z.literal('cancelled_order'),
] as const;
export const reasonSchema = z.union(reasons);
export type Reason = z.infer<typeof reasonSchema>;

export const labels = [
  'Duplicate',
  'Fraudulent',
  'Requested by customer',
  'Requested by seller',
  'Product return',
  'Cancelled order',
];

export const ALL_REASONS = reasons.map((reason) => reason.value);

type refundOrderArgs = {
  order_id: string;
  intent_id: string;
  reason: Reason;
  product_ids: string[];
  partial_amount?: number;
  /**
   * api#388 ruling B. Set only when the operator explicitly acknowledged a
   * prior refund ("this was already issued, issue another?") — names the
   * PRIOR intent id being deliberately duplicated, for the server's own
   * audit trail (functions/src/orders/utils/refundOrder.ts:47-56). Always
   * paired with a brand-new `intent_id`, never the one that was just
   * reported back as reused.
   */
  allow_duplicate_of?: string;
};

/**
 * GB api#388. This calls `refundOrderV2`, not the legacy `refundOrder`.
 *
 * The legacy callable was handed the whole parsed `order` object plus
 * `payment_id` and `sellers`, and wrote that client-supplied state back to
 * Firestore. A page fetched before some other write (the order detail page uses
 * a one-shot `getDoc`, and the app sets `refetchOnWindowFocus: false`) could
 * therefore clobber a payout's `transfer_id` and cause a second payment.
 *
 * V2 accepts identifiers only and reads order state server-side. It REJECTS a
 * payload carrying `order`, `payment_id`, or `sellers` at all — an explicit key
 * check, not schema stripping (functions/src/orders/utils/refundOrder.ts:353-360)
 * — so this argument type is deliberately narrow and must stay that way.
 *
 * `partial_amount` is in DOLLARS, not cents; the server multiplies by 100 and
 * rejects any value that is not a whole cent (refundOrder.ts:397-419).
 */
export async function refundOrder(args: refundOrderArgs) {
  const res = await httpsCallable<refundOrderArgs, unknown>(
    functions,
    'refundOrderV2',
    // GB api#388 (QA F1). The Firebase client hard-defaults its deadline to
    // 70s (@firebase/functions dist/index.cjs.js:574), but refundOrderV2 is
    // declared `timeoutSeconds: 120`. Legacy `refundOrder` set no
    // timeoutSeconds, so it inherited the 60s GCF default and the SERVER
    // always expired first — the client therefore always received a real,
    // classified error. Repointing to V2 silently inverted that.
    //
    // The 70-120s window is reachable: `refunds.findByIntent` pages Stripe's
    // whole refund history 100 at a time, and label voids run deliberately
    // serially. In that window the client aborts with `deadline-exceeded`,
    // which carries no `refund_stage` marker, so a refund that SUCCEEDED
    // renders the persistent "do not create a new refund" alarm — the exact
    // false money alarm this change exists to remove.
    //
    // 130s, not 120s: keep the server strictly the first to expire, which is
    // the property that made the legacy pairing safe.
    { timeout: 130_000 }
  )(args);
  // Compare against the SERVER'S id for this intent, which is the composite
  // `${order_id}_${intent_id}` — not the raw `args.intent_id` we sent. See the
  // long note on `refundIntentDocumentId`. Getting this wrong makes every
  // successful refund look ambiguous to the operator.
  return parseRefundOrderResult(
    res.data,
    refundIntentDocumentId(args.order_id, args.intent_id)
  );
}

/**
 * web#388 ruling D1b (admin nav badge). Counts the WHOLE admin
 * refund-reconciliation queue — payout holds AND failed labels, conflicts,
 * and tax follow-ups — per Bill's ruling ("Refunds needing attention (N)").
 * Do NOT filter this down to `payout_hold_count > 0`; that narrower framing
 * was considered and rejected as a worse instrument that hides real
 * incidents (a bad label, a stuck reconciliation, or a tax follow-up with no
 * payout hold at all would all read as "nothing to see").
 *
 * `listRefundReconciliationWork` (functions/src/index.ts:3121 in
 * gearfocus-api) is already deployed on both prod and staging, admin-gated,
 * read-only, and paginated — no api change needed for this badge. It has no
 * dedicated "total count" field, so this asks for its own max page size
 * (200) and counts what comes back; a queue deeper than 200 undercounts
 * rather than throwing, which is the right failure direction for a nav
 * badge — an admin who opens the actual queue still sees everything via its
 * own pagination, this just under-advertises it in the meantime.
 */
export async function getRefundReconciliationQueueCount(): Promise<number> {
  const res = await httpsCallable<{ limit: number }, unknown>(
    functions,
    'listRefundReconciliationWork'
  )({ limit: 200 });
  const data = res.data;
  if (
    !data ||
    typeof data !== 'object' ||
    !Array.isArray((data as { work?: unknown }).work)
  ) {
    throw new Error(
      'listRefundReconciliationWork returned an unexpected shape'
    );
  }
  return (data as { work: unknown[] }).work.length;
}

/**
 * web#388 item 4 (web half) — the payout-hold release action ("Mark payout
 * handled"). Calls the admin-only, audited `releaseRefundPayoutHold`
 * callable, which clears a `partial_refund` payout hold and its
 * reconciliation-queue entry. It does NOT make the item payable —
 * `refund_amount` is untouched, so the live payout formula (which has no
 * refund term at all) still cannot pay it. Never call this for any hold
 * reason other than `partial_refund` — the server rejects it
 * (`hold_reason_not_partial_refund`), and the UI must not offer the button
 * for those cases at all (see OrderDetailsPage.tsx's `canReleasePayoutHold`).
 */
export type ReleaseRefundPayoutHoldArgs = {
  order_id: string;
  seller_id: string;
  item_id: string;
  note?: string;
};

// The response type is defined by the zod contract in refundOrder.contract.ts
// (`ReleaseRefundPayoutHoldResult`), re-exported below — do not redeclare it
// here as a plain interface, which would let the runtime parse and the
// compile-time type drift apart silently.
export type { ReleaseRefundPayoutHoldResult } from './refundOrder.contract';

/**
 * The full set of `reason` values the api can put in the error `details`.
 * Kept here (not just in refundCopy.ts) so `extractReleasePayoutHoldErrorReason`
 * can narrow against it — an unrecognized reason string degrades to the
 * generic message rather than being trusted blindly.
 */
export const RELEASE_PAYOUT_HOLD_ERROR_REASONS = [
  'not_admin',
  'order_not_found',
  'item_not_found',
  'item_ambiguous',
  'hold_reason_not_partial_refund',
  'reconciliation_required',
  'refund_lock_present',
  'already_paid',
  // gearfocus-api CTO gate finding L2 (a corrupted payout_hold_ref pointing
  // at another order's intent was silently stripping the hold from that
  // UNRELATED order). Added server-side to
  // ReleaseRefundPayoutHoldReason (refundOrder.ts:1980-1988) — kept in sync
  // here so extractReleasePayoutHoldErrorReason's whitelist recognizes it
  // instead of degrading a data-corruption alarm into the generic "try
  // again" message (retrying this one specifically can never help; see
  // RELEASE_PAYOUT_HOLD_ERROR_MESSAGE in refundCopy.ts).
  'corrupt_payout_hold_ref',
  // Codex P1 (api#388, missed by 4 prior adversarial gates, independently
  // verified by the orchestrator against the server source before this
  // string was added here). The release action deliberately accepts
  // legacy/missing-intent partial_refund holds, so an item can carry
  // payout_hold_reason: "partial_refund" while missing one or both of the
  // PERMANENT guards that are supposed to survive the release
  // (order.refund_payout_holds[itemKey], and a non-zero item.refund_amount
  // — payoutExecutor.ts's hasBlockingRefundAmount treats an ABSENT
  // refund_amount as non-blocking, not as a block). Clearing the
  // visibility fields on such an item would leave it payable at its
  // refund-blind full amount — the exact overpayment this whole system
  // exists to prevent. The server now fails closed and refuses instead
  // (refundOrder.ts:1980-1989, thrown at :2271-2280) — kept in sync here
  // for the same reason corrupt_payout_hold_ref is: this is a data-
  // integrity refusal, not a retryable failure, and must never degrade to
  // the generic "try again" message (see RELEASE_PAYOUT_HOLD_ERROR_MESSAGE
  // in refundCopy.ts).
  'missing_permanent_refund_guard',
] as const;
export type ReleaseRefundPayoutHoldErrorReason =
  (typeof RELEASE_PAYOUT_HOLD_ERROR_REASONS)[number];

/**
 * Firebase callable errors carry their structured payload on `.details`, not
 * on the error message — narrowed at runtime rather than trusted by type,
 * same convention as `refundErrorDefinitelyBeforeStripe`
 * (refundIntentStorage.ts) and `payoutHoldLine` (OrderDetailsPage.tsx): an
 * unexpected shape degrades to `undefined` (the generic message), never a
 * throw or a fabricated reason.
 */
export const extractReleasePayoutHoldErrorReason = (
  error: unknown
): ReleaseRefundPayoutHoldErrorReason | undefined => {
  if (!error || typeof error !== 'object' || !('details' in error)) {
    return undefined;
  }
  const details = (error as { details?: unknown }).details;
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return undefined;
  }
  const reason = (details as { reason?: unknown }).reason;
  return typeof reason === 'string' &&
    (RELEASE_PAYOUT_HOLD_ERROR_REASONS as readonly string[]).includes(reason)
    ? (reason as ReleaseRefundPayoutHoldErrorReason)
    : undefined;
};

export async function releaseRefundPayoutHold(
  args: ReleaseRefundPayoutHoldArgs
): Promise<ReleaseRefundPayoutHoldResult> {
  const res = await httpsCallable<ReleaseRefundPayoutHoldArgs, unknown>(
    functions,
    'releaseRefundPayoutHold',
    // A small in-transaction write (two field clears plus a recompute
    // through refundIntentNeedsAttention — see the task doc's item-4
    // correction), not the multi-stage Stripe/label/tax pipeline
    // refundOrderV2 is. 30s, well under the client's 70s default deadline,
    // is generous for this shape of call.
    { timeout: 30_000 }
  )(args);
  // Runtime-parsed the same way refundOrder() parses its response above —
  // this callable is admin-only and money-adjacent, so a shape the client
  // has never seen should surface as an error, not be trusted blindly.
  return parseReleaseRefundPayoutHoldResult(res.data);
}

// Processing fee changed from 3% to 3.5% on April 1, 2026.
// Use order date to apply the correct historical rate.
const FEE_INCREASE_DATE = new Date('2026-04-01').getTime();

// refundOrder.ts:1180-1184 (gearfocus-api) projects allocations.full_item to
// cumulative_charge_v1 + state 5. Sellers cannot read refund_intents directly.
export const isFullyRefundedForSellerSummary = (item: OrderItemDocument) => {
  if (item.refund_model === 'cumulative_charge_v1') return item.state === 5;
  const refunded = item.refund_amount ?? 0;
  const total =
    item.product_cost * item.qty + item.buyer_shipping_cost + (item.product_tax ?? 0);
  // Legacy labels retain fractional cents while settled refunds use cents.
  return refunded > 0 && Math.round(refunded * 100) >= Math.round(total * 100);
};

export const calculateEarnings = (
  item: OrderItemDocument,
  buyerState = '',
  sellerTaxStates: string[] = [],
  fee = 0.05,
  orderCreated?: number,
  accountForRefund = false
) => {
  const subtotal = item.product_cost * item.qty;
  const total = subtotal + item.buyer_shipping_cost + (item.product_tax || 0);
  const processingRate =
    orderCreated && orderCreated < FEE_INCREASE_DATE ? 0.03 : 0.035;
  const processing = total * processingRate;
  const shipping =
    !item.rate_id && item.buyer_shipping_cost ? item.buyer_shipping_cost : 0;
  const label_cost = item.rate_id ? item.buyer_shipping_cost : 0;
  const commission = fee * (shipping + subtotal);
  const sellerRemits = sellerTaxStates.includes(buyerState);
  const taxes = sellerRemits ? 0 : (item.product_tax ?? 0);
  const ins_amount = item.insurance_amount ?? 0;
  // Only the seller summary opts in; admin displays keep their existing math.
  if (accountForRefund && isFullyRefundedForSellerSummary(item)) {
    return [0, 0, taxes];
  }
  // Partial refunds keep legacy earnings pending the bearer-policy decision in #2070.
  const net = total - processing - commission - label_cost - taxes - ins_amount;
  return [net, commission, taxes];
};
